// The bank of the SSAS form of the client application (SEP-293).
//
// THE SOURCE IS THE FIRM'S OWN FORM: `DGW SSAS App Form (Disc/Adv/XO) v1.0
// 0725`, the form of a small self-administered scheme. The text is
// `docs/sources/sep-293-app-ssas.txt`, and the question list is
// `docs/sources/sep-293-ssas-questions.md`. The rules H1 to H17 of the design
// apply on top of the question list.
//
// # ONE FORM FOR EACH SERVICE
//
// The paper form prints the three services. Parts 4, 8 and 9 do not need to
// be completed for the Execution Only service, so each question of those
// parts shows only for the Discretionary Managed service and the Advisory
// service, as on the trust form.
//
// # THE APPLICANT IS THE LEAD INDIVIDUAL
//
// The person who fills in the form is the lead individual of the scheme
// (Part 1), the main contact for the account. The lead individual answers
// under the ids of the individual form (rule H1), with the address history
// of the individual form 3.0, as on the company and the trust forms. The
// paper form prints one telephone number, which is `mobile_number` (rule H5).
// The form has no nominated representative.
//
// The other members of the scheme (Part 2) are a person group, `member_<n>_*`,
// of seven blocks. A scheme can have one member only, so block 1 opens with
// its own yes or no question (rule H3).
//
// Part 3 names the scheme and its provider (the pension trustee). The provider
// is a firm: it is not a party of the identity check, and its identifiers are
// not the identifiers of the scheme (open question Q20).
//
// Part 4 asks the personal finances of the lead individual under the ids of
// the individual form 2.0, with one figure for the pension assets (open
// question Q21).
//
// # WHAT IS NOT HERE
//
//   - The signatures (Part 14). The submission act replaces the signature of
//     the lead individual. The firm gets the signatures of the other members
//     and of the pension trustee off-line.
//   - The signatory list of the pension trustee, the bank statement and
//     Appendix I (rule H14).
//   - Declaration 6c, "Schedule of Charges" (rule H8).
//
// # THE WORDING IS DRAFT
//
// Each label of the paper form is the firm's draft copy. The declarations
// say "the Trust" and "Part 15", as the paper form prints them, until the
// firm confirms other words (open question Q22). The risk questions use the
// five-point scale of the individual form (open question Q12).
//
// # THE IDS ARE FROZEN
//
// `FROZEN_CLIENT_APPLICATION_IDS_BY_FORM` in `client-application-frozen-ids.ts`
// pins the ids of the bank. A slice may add an id, and it may never drop or
// rename one.

import {
    addressQuestions,
    individualQuestion,
    individualSectionQuestions,
    personGroup,
    questionChanges,
    sharedServiceTypeSection,
} from './client-application-blocks';
import {
    ENTITY_BANK_RULES,
    entityAccountUsageQuestions,
    entityAdditionalNotesSection,
    entityApplicantQuestions,
    entityDeclarationQuestions,
    entityDeclarationsSection,
    entityHealthSection,
    entityNationalClientIdentifierExplainer,
    entityObjectivesQuestions,
    entityPortalSecurityQuestions,
    entityThirdPartyQuestions,
} from './client-application-form-company';
import { onlyForManagedServices } from './client-application-form-trust';
import type {
    ClientApplicationBank,
    ClientApplicationQuestion,
    ClientApplicationSection,
    ClientApplicationStatement,
} from './client-application-questions';

/** The version of the SSAS form, from the footer of the paper form. */
export const SSAS_FORM_VERSIONS = ['1.0'] as const;

/** The number of person blocks of Part 2. */
export const SSAS_MEMBER_COUNT = 7;
/** The id prefix of the other members of the scheme (Part 2). */
export const SSAS_MEMBER_PREFIX = 'member';

/** The label of the National Client Identifier: the SSAS form names Appendix II. */
export const PENSION_NATIONAL_CLIENT_IDENTIFIER_LABEL =
    'National Client Identifier (see Appendix II. If UK national, use NI no.)';

/** Appendix II of the SSAS and the SIPP forms. */
export const PENSION_NATIONAL_CLIENT_IDENTIFIER_EXPLAINER =
    entityNationalClientIdentifierExplainer('clients');

// ---------------------------------------------------------------------------
// Part 1, the lead individual
// ---------------------------------------------------------------------------

/** The description of each service, page 1 of the form. */
const SERVICE_NOTES: readonly ClientApplicationStatement[] = [
    {
        id: 'ssas_service_note_discretionary',
        text: 'Discretionary Managed service: A professional investment manager makes investment decisions for the SSAS without needing your approval each time.',
    },
    {
        id: 'ssas_service_note_advisory',
        text: 'Advisory service: You get advice from a professional investment manager, but you make the final decision on your SSAS investments and execute trades yourself.',
    },
    {
        id: 'ssas_service_note_execution_only',
        text: 'Execution only service: Dowgate Wealth is only responsible for executing the trades as requested by you. We will not offer any investment advice or recommendations.',
    },
];

const APPLICANT_SECTION: ClientApplicationSection = {
    id: 'applicant',
    part: 1,
    title: 'Lead individual - personal information',
    stepLabel: 'Lead individual',
    intro: 'This section is to be completed by the Lead Individual who will be the main contact for the account and will make investment decisions on behalf of the SSAS.',
    notes: SERVICE_NOTES,
    questions: entityApplicantQuestions({
        nationalClientIdentifierExplainer: PENSION_NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
        nationalClientIdentifierLabel: PENSION_NATIONAL_CLIENT_IDENTIFIER_LABEL,
        phones: 'one',
    }),
};

// ---------------------------------------------------------------------------
// Part 2, the other members
// ---------------------------------------------------------------------------

const MEMBERS_SECTION: ClientApplicationSection = {
    id: 'ssas_members',
    part: 2,
    title: 'Personal information',
    stepLabel: 'Members',
    intro: 'This section is to be completed by all other members of the SSAS. If needed, please use the Additional Information section at the back of the form to add further details.',
    questions: personGroup({
        prefix: SSAS_MEMBER_PREFIX,
        count: SSAS_MEMBER_COUNT,
        heading: (n) => `Personal information (${n})`,
        firstOpenerLabel: 'Does the SSAS have other members?',
        openerLabel: 'Does the SSAS have another member to add?',
        phones: 'one',
        nationalClientIdentifierExplainer: PENSION_NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
        nationalClientIdentifierLabel: PENSION_NATIONAL_CLIENT_IDENTIFIER_LABEL,
    }),
};

// ---------------------------------------------------------------------------
// Part 3, the scheme and its provider
// ---------------------------------------------------------------------------

const PROVIDER = 'Provider details';
const CONTACT = 'SSAS provider main contact';

const SCHEME_SECTION: ClientApplicationSection = {
    id: 'scheme_details',
    part: 3,
    title: 'Pension trustee details',
    stepLabel: 'Scheme',
    intro: 'Please provide information about your SSAS Provider/Pension Trustees.',
    questions: [
        {
            id: 'scheme_name',
            kind: 'short_text',
            label: 'Pension Scheme Name',
            required: true,
            group: 'SSAS details',
        },
        {
            id: 'scheme_hmrc_pension_number',
            kind: 'short_text',
            label: 'HMRC Pension number',
            required: true,
            group: 'SSAS details',
        },
        {
            id: 'scheme_administrator_name',
            kind: 'short_text',
            label: 'Name of SSAS Provider',
            required: true,
            group: PROVIDER,
        },
        {
            id: 'scheme_administrator_status',
            kind: 'short_text',
            label: 'Status (Ltd, Plc, LLP etc)',
            group: PROVIDER,
        },
        {
            id: 'scheme_administrator_date_of_incorporation',
            kind: 'date',
            label: 'Date of incorporation',
            group: PROVIDER,
        },
        {
            id: 'scheme_administrator_country_of_incorporation',
            kind: 'country_select',
            label: 'Country of incorporation',
            group: PROVIDER,
        },
        {
            id: 'scheme_administrator_lei',
            kind: 'short_text',
            label: 'Legal Entity Identifier (LEI)',
            group: PROVIDER,
        },
        {
            id: 'scheme_administrator_registration_number',
            kind: 'short_text',
            label: 'Registration number',
            group: PROVIDER,
        },
        {
            id: 'scheme_administrator_contact_surname',
            kind: 'short_text',
            label: 'Surname',
            group: CONTACT,
        },
        {
            id: 'scheme_administrator_contact_forenames',
            kind: 'short_text',
            label: 'Forename(s)',
            group: CONTACT,
        },
        {
            id: 'scheme_administrator_contact_phone',
            kind: 'short_text',
            label: 'Phone number',
            textFormat: 'phone',
            group: CONTACT,
        },
        {
            id: 'scheme_administrator_contact_email',
            kind: 'short_text',
            label: 'Email',
            textFormat: 'email',
            group: CONTACT,
        },
        ...addressQuestions({
            prefix: 'scheme_administrator_registered_address',
            group: 'Registered address',
            required: true,
            houseLabel: 'Building no./name',
        }),
        {
            // The paper form prints "CORRESPONDENCE ADDRESS (if different to
            // Registered Address)" and no question. The online form asks.
            id: 'scheme_administrator_correspondence_address_different',
            kind: 'yes_no',
            label: 'Is the correspondence address different to the registered address?',
            required: true,
            group: 'Correspondence address',
        },
        ...addressQuestions({
            prefix: 'scheme_administrator_correspondence_address',
            group: 'Correspondence address',
            required: true,
            houseLabel: 'Building no./name',
            showWhen: {
                questionId: 'scheme_administrator_correspondence_address_different',
                equals: ['Yes'],
            },
        }),
    ],
};

// ---------------------------------------------------------------------------
// Part 4, the financial situation of the lead individual (managed services)
// ---------------------------------------------------------------------------

/** The rows of Part 4 of the SSAS and the SIPP forms, from the individual form 2.0. */
const PENSION_FINANCIAL_IDS = {
    incomeAndProperty: [
        'income_employment',
        'income_investment_outside_dowgate',
        'income_state_pension',
        'income_other_pension',
        'income_all_other',
        'income_all_other_sources',
        'commitment_mortgage_rent',
        'commitment_essential_expenses',
        'commitment_school_fees',
        'commitment_discretionary_expenses',
        'commitment_other_significant_expenses',
        'commitment_other_significant_expenses_detail',
        'asset_main_property_value',
        'asset_other_property_value',
        'asset_cash_savings_premium_bonds',
    ],
    otherAssetsAndLiabilities: [
        'asset_business_interests',
        'asset_other_investments_value',
        'asset_other',
        'asset_other_detail',
        'liability_credit_card_debt',
        'liability_mortgage_outstanding',
        'liability_anticipated_mortgage_payoff_date',
        'liability_other_debt',
        'liability_other_debt_detail',
    ],
} as const;

/**
 * The questions of Part 4 of the SSAS and the SIPP forms: the personal
 * finances of the person, as the individual form 2.0 asks them, with one
 * figure for the pension assets. The paper form asks no other question to
 * open a row, so each row shows for the managed services, and each row needs
 * an answer, as each question of the form does.
 */
export function pensionFinancialQuestions(): ClientApplicationQuestion[] {
    const take = (ids: readonly string[]) =>
        individualSectionQuestions(
            'financial_situation',
            '2.0',
            ids,
            Object.fromEntries(ids.map((id) => [id, { dropShowWhen: true }])),
        );
    const pension: ClientApplicationQuestion = {
        id: 'asset_pension_assets',
        kind: 'currency',
        label: 'Pension assets',
        group: individualQuestion('asset_main_property_value', '2.0').group,
    };
    return [
        ...take(PENSION_FINANCIAL_IDS.incomeAndProperty),
        pension,
        ...take(PENSION_FINANCIAL_IDS.otherAssetsAndLiabilities),
    ].map(onlyForManagedServices);
}

const FINANCIAL_SECTION: ClientApplicationSection = {
    id: 'financial_situation',
    part: 4,
    title: 'Financial situation',
    stepLabel: 'Finances',
    intro: 'To be completed by the Lead Individual. This section does not need to be completed if you have chosen an Execution Only service.',
    questions: pensionFinancialQuestions(),
};

// ---------------------------------------------------------------------------
// Parts 5 to 9
// ---------------------------------------------------------------------------

/** The source table of the individual form, with its own prompts. */
export function pensionSourceOfWealthQuestions(): ClientApplicationQuestion[] {
    const keys = [
        'employment',
        'savings_investments',
        'inheritance',
        'gift',
        'property',
        'business',
        'other',
    ];
    return individualSectionQuestions('source_of_wealth_and_funds', '3.0', [
        'source_types',
        ...keys.flatMap((key) => [
            `source_of_wealth_${key}_detail`,
            `source_of_funds_${key}_detail`,
        ]),
    ]);
}

/** The risk questions of the individual form 2.0, without the intended risk profile. */
export function pensionRiskQuestions(): ClientApplicationQuestion[] {
    const ids = [
        'risk_protection_over_returns',
        'risk_prefer_fewer_ups_and_downs',
        'risk_comfortable_with_smaller_companies',
        'risk_understands_smaller_company_risks',
        'risk_response_to_quarter_fall',
        'risk_loss_that_prompts_move_to_cash',
        'risk_investment_horizon',
        'risk_income_security',
        'risk_income_change_over_ten_years',
        'risk_liquid_funds_months_of_expenses',
        'risk_capacity_for_loss',
        'risk_liabilities_change_over_ten_years',
    ];
    return individualSectionQuestions(
        'risk_tolerance_and_capacity_for_loss',
        '2.0',
        ids,
        Object.fromEntries(ids.map((id) => [id, { dropExplainer: true }])),
    ).map(onlyForManagedServices);
}

/** The client classification notes of the SSAS and the SIPP forms. */
export function pensionClassificationNotes(prefix: string): ClientApplicationStatement[] {
    return [
        {
            id: `${prefix}_client_classification_note_1`,
            text: 'To provide the best possible services and outcomes we need to assign a client classification to every client - which are investor categories defined by the financial regulator. This classification helps us understand your financial knowledge and needs, ensuring we offer you the right products and advice. It also ensures that we follow the necessary regulations to protect you based on your experience and the amount of money you’re investing.',
        },
        {
            id: `${prefix}_client_classification_note_2`,
            text: 'Please note, we will classify you as a retail client unless you strongly feel you are a professional client. Please see the descriptions below and inform your Investment Manager if you feel you fall outside of the retail client classification.',
        },
        {
            id: `${prefix}_client_classification_note_3`,
            text: 'Retail client: An individual who uses financial services for personal or non-professional purposes, typically with less experience or knowledge of investing.',
        },
        {
            id: `${prefix}_client_classification_note_4`,
            text: 'Professional client: An individual with a higher level of investment knowledge and experience, and whose career may have been in, or related to, financial markets.',
        },
    ];
}

/** The objectives and the experience of the person, in the words of the individual form. */
export function pensionObjectivesQuestions(): ClientApplicationQuestion[] {
    return entityObjectivesQuestions({
        objectiveLabel: 'What best describes your investment objective?',
        restrictionsLabel:
            'If you have any ethical, environmental, geographic, sector, or stock specific restrictions, please provide these in as much detail as possible below:',
    }).map(onlyForManagedServices);
}

/** The seven questions of the bank account, with the label of the pension forms. */
export function pensionBankAccountQuestions(): ClientApplicationQuestion[] {
    return individualSectionQuestions(
        'bank_account_information',
        '3.0',
        [
            'bank_name',
            'bank_account_name',
            'bank_sort_code',
            'bank_account_number',
            'bank_iban',
            'bank_bic_aba',
            'bank_account_currency',
        ],
        questionChanges([['bank_account_name', { label: 'Account Holder Name' }]]),
    );
}

const LEAD = 'To be completed by the Lead Individual.';

const SSAS_DECLARATIONS: readonly ClientApplicationStatement[] = [
    {
        id: 'ssas_declaration_1',
        text: 'I have read and understood the nature and the risk of the product(s) that the Trust intends to invest in through this account.',
    },
    {
        id: 'ssas_declaration_2',
        text: 'I act on behalf of the Trust as specified on this application form and not on behalf of another party in respect of all matters related to this account. Accordingly, all funds to be deposited and transacted on the account are the funds of the Trust.',
    },
    {
        id: 'ssas_declaration_3',
        text: 'I am not aware of any health or other conditions that I may have that might affect the proper operation of the account on behalf of the Trust.',
    },
    {
        id: 'ssas_declaration_4',
        text: 'I have provided true, accurate and complete information and undertake to update Dowgate Wealth of any changes to the information provided without delay.',
    },
    {
        id: 'ssas_declaration_5',
        text: 'I accept that in certain circumstances Dowgate Wealth will be obliged to share information with UK tax authorities, who may pass it on to other tax authorities.',
    },
    { id: 'ssas_declaration_6', text: 'I have received, read and understood the following:' },
    {
        id: 'ssas_declaration_6a',
        text: 'a. Dowgate Wealth Terms of Business - including the product risks disclosure (website link: https://dowgatewealth.co.uk/terms-of-business/)',
    },
    {
        id: 'ssas_declaration_6b',
        text: 'b. Conflict of Interest Policy (website link: https://dowgatewealth.co.uk/conflicts-of-interest-policy/)',
    },
    // Rule H8: "c. Schedule of Charges" is removed on purpose, as on the
    // individual form (SEP-263.39). The lettering stays a, b, d.
    {
        id: 'ssas_declaration_6d',
        text: 'd. I have obtained the Dowgate Wealth Order Execution Policy (website link: https://dowgatewealth.co.uk/dowgate-wealth-ltd-order-execution-policy/)',
    },
    {
        id: 'ssas_declaration_7',
        text: 'I consent to the Order Execution Policy and for any orders to be executed outside a regulated market or a multilateral trading facility.',
    },
    {
        id: 'ssas_declaration_8',
        text: 'I consent for any unexecuted limit orders not to be made public.',
    },
    {
        id: 'ssas_declaration_9',
        text: 'I accept and agree to be bound by the terms provided above and consent to such terms and information including future updates to these be provided to me by way of posting on the websites indicated above.',
    },
];

// ---------------------------------------------------------------------------
// The bank
// ---------------------------------------------------------------------------

/** The bank of the SSAS form, `DGW SSAS App Form (Disc/Adv/XO) v1.0`. */
export const SSAS_BANK: ClientApplicationBank = {
    versions: SSAS_FORM_VERSIONS,
    sections: [
        sharedServiceTypeSection(),
        APPLICANT_SECTION,
        MEMBERS_SECTION,
        SCHEME_SECTION,
        FINANCIAL_SECTION,
        entityHealthSection(5, LEAD),
        {
            id: 'source_of_wealth_and_funds',
            part: 6,
            title: 'Source of wealth & funds',
            stepLabel: 'Wealth & funds',
            intro: `In very simple terms, source of wealth is about how you earned your wealth over time, while source of funds is about where the money you’ll be using to invest comes from. We need to understand both of these elements for a clearer picture of your financial situation, so we can manage your investments and service your needs in the best way. These requirements are also part of the UK’s broader anti-money laundering regulations and help ensure the integrity of the financial system. ${LEAD}`,
            questions: pensionSourceOfWealthQuestions(),
        },
        {
            id: 'account_type_and_usage',
            part: 7,
            title: 'Account type & usage',
            stepLabel: 'Account usage',
            intro: `It’s important for us to understand your expectations regarding funding and withdrawals to and from your portfolio, so we can plan investments accordingly. It’s also a regulatory requirement and additional protection for you if, for example, we observe any unusual activity. ${LEAD}`,
            questions: entityAccountUsageQuestions({
                regular:
                    'For example, dividends paid out monthly or quarterly to your personal bank account',
                capital: 'For example, a property purchase is planned for next year',
                funding:
                    'For example, transfer of funds from an annual bonus to your investment account',
            }),
        },
        {
            id: 'investment_objectives_and_experience',
            part: 8,
            title: 'Investment objectives & experience',
            stepLabel: 'Objectives',
            intro: `It’s important for us to understand your objectives and any previous investment experience you have had. Not only is this a UK regulatory requirement. It also helps ensure the portfolio we build for you is suitable, and the advice and way we communicate with you is appropriate. ${LEAD} This section does not need to be completed if you have chosen an Execution Only account.`,
            notes: pensionClassificationNotes('ssas'),
            questions: pensionObjectivesQuestions(),
        },
        {
            id: 'risk_tolerance_and_capacity_for_loss',
            part: 9,
            title: 'Risk tolerance & capacity for loss',
            stepLabel: 'Risk',
            intro: `Whether it’s your first investment or you’re an experienced investor, grasping the relationship between risk and return is fundamental to making informed decisions about your financial future. The questions in this section help us evaluate your tolerance and capacity for risk, ensuring we apply the appropriate risk profile to your portfolio. You can find out more in our Risk and Investment guide and Capacity for Loss guide. ${LEAD} This section does not need to be completed if you have chosen an Execution Only account.`,
            questions: pensionRiskQuestions(),
        },
        {
            id: 'bank_account_information',
            part: 10,
            title: 'Bank account information',
            stepLabel: 'Bank account',
            intro: 'Please note that payments will only be made to and accepted from your nominated bank account detailed below and that a certified copy of a bank statement will be required. For non-UK bank accounts, please provide IBAN, BIC / ABA numbers and the account currency.',
            questions: pensionBankAccountQuestions(),
        },
        {
            id: 'third_party_disclosure',
            part: 11,
            title: 'Third party information',
            stepLabel: 'Third party',
            intro: 'In certain situations, it is useful to have copies of certain reports and documentation relating to your account(s) sent to third parties. For example a tax advisor for assessment and tax filing purposes. If you would like Dowgate Wealth to provide information regarding the account to a third-party individual, please provide the relevant details below.',
            questions: entityThirdPartyQuestions(),
        },
        {
            id: 'online_portal_security_questions',
            part: 12,
            title: 'Online portal security questions',
            stepLabel: 'Portal security',
            intro: 'Our online portal gives you access to your investment portfolio, so you can see your holdings, the value of your investments and a history of all your transactions. For security, we ask the Lead Individual to provide answers to the following questions. Additional access can be provided upon request for third parties.',
            questions: entityPortalSecurityQuestions(),
        },
        entityAdditionalNotesSection(
            'If you need to add more information – for example additional third-party information requirements – please use the additional notes section at the end of this form.',
        ),
        entityDeclarationsSection(
            13,
            entityDeclarationQuestions({
                statements: SSAS_DECLARATIONS,
                explainer:
                    'The other members of the SSAS and the Pension Trustee must also sign. We will contact you to arrange these signatures.',
                marketingExplainer:
                    'We offer company news and market commentary which is emailed to the contact email address detailed in Section 1. If you would like to receive this, please select your preferences:',
            }),
        ),
    ],
    ...ENTITY_BANK_RULES,
};
